// frontend/src/services/assignmentService.js
// Fetches assignment-backed UPS read models for either a specific site or all company sites.
import axios from 'axios';

const ALLOWED_TIME_BUCKETS = new Set(['today', 'scheduled', 'historical']);

const normalizeSourceParam = (source) => {
  const value = String(source || 'all').toLowerCase();
  if (value === 'agency') return 'agency';
  if (value === 'internal') return 'internal';
  return undefined;
};

function buildAssignmentReadParams({
  statuses = [],
  agencyId = '',
  search = '',
  source = 'all',
  timeBucket,
} = {}) {
  const params = {};

  if (Array.isArray(statuses) && statuses.length) {
    params.statuses = statuses.join(',');
  }
  if (agencyId) params.agency_id = agencyId;
  if (search) params.search = search;

  const sourceParam = normalizeSourceParam(source);
  if (sourceParam) {
    params.source = sourceParam;
  }

  if (timeBucket && ALLOWED_TIME_BUCKETS.has(timeBucket)) {
    params.time_bucket = timeBucket;
  }

  return params;
}

/**
 * Fetch assignment rows for a specific site.
 */
export async function fetchSiteAssignments(
  siteId,
  options = {}
) {
  if (siteId == null) return [];

  const params = buildAssignmentReadParams(options);

  const { data } = await axios.get(`/ups/sites/${Number(siteId)}/assignments`, {
    params,
  });

  return Array.isArray(data) ? data : [];
}

/**
 * Fetch assignment rows across all company sites.
 */
export async function fetchCompanyAssignments(options = {}) {
  const params = buildAssignmentReadParams(options);

  const { data } = await axios.get('/ups/assignments', { params });

  return Array.isArray(data) ? data : [];
}

/**
 * Phase 8.7B — orphaned / blocked assignments for a specific site.
 */
export async function fetchOrphanedAssignments(
  siteId,
  options = {}
) {
  if (siteId == null) return [];

  const params = buildAssignmentReadParams(options);

  const { data } = await axios.get(
    `/ups/sites/${Number(siteId)}/assignments/orphaned`,
    { params }
  );

  return Array.isArray(data) ? data : [];
}

/**
 * Phase 3A — orphaned / blocked assignments across all company sites.
 */
export async function fetchCompanyOrphanedAssignments(options = {}) {
  const params = buildAssignmentReadParams(options);

  const { data } = await axios.get('/ups/assignments/orphaned', { params });

  return Array.isArray(data) ? data : [];
}

// Phase 8.7C — cleanup-only actions (UPS-admin)
export async function completeAssignment(siteId, assignmentId) {
  const { data } = await axios.patch(
    `/ups/sites/${Number(siteId)}/assignments/${Number(assignmentId)}/complete`
  );
  return data?.assignment || null;
}

export async function cancelAssignment(siteId, assignmentId) {
  const { data } = await axios.patch(
    `/ups/sites/${Number(siteId)}/assignments/${Number(assignmentId)}/cancel`
  );
  return data?.assignment || null;
}

// Phase 8.7D — UPS approval (requested -> confirmed)
export async function approveAssignment(siteId, assignmentId) {
  const { data } = await axios.patch(
    `/ups/sites/${Number(siteId)}/assignments/${Number(assignmentId)}/approve`
  );
  return data?.assignment || null;
}

// Phase 9.1 / Step 2D — move an open assignment to another site
export async function moveAssignment(
  siteId,
  assignmentId,
  { target_site_id, target_status } = {}
) {
  const payload = {};

  if (target_site_id != null) {
    payload.target_site_id = Number(target_site_id);
  }
  if (target_status) {
    payload.target_status = target_status;
  }

  const { data } = await axios.patch(
    `/ups/sites/${Number(siteId)}/assignments/${Number(assignmentId)}/move`,
    payload
  );

  return data?.assignment || data || null;
}